import { useEffect, useMemo, useState } from 'react';
import { Lock, LogOut, User, ShoppingCart, ArrowRightLeft, RefreshCw, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/stores/auth-store';
import { useTrial } from '@/hooks/use-trial';
import { useAdminDetails } from '@/hooks/use-admin-details';
import { PlansBrowser } from '@/components/settings/plans-browser';
import { Button } from '@/components/ui/button';
import { clearAllCache } from '@/lib/api/proxy';

/**
 * Full-screen non-dismissible paywall modal.
 * Shown when the 10-day trial has expired and the user has no active subscription.
 *
 * - Cannot be closed (no Escape, no backdrop click, no close button)
 * - z-index 10000000 to cover everything (matching old app behavior)
 * - Shows current user + logout option at the top
 * - Embeds PlansBrowser inline so users can purchase without leaving the app
 * - Shows admin contact info for shared-access users
 */
export function TrialPaywallModal() {
  const { shouldShowPaywall, isSharedAccess, trialDaysRemaining, hasCancelledSubscription } = useTrial();
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const selectedAdminUserEmail = useAuthStore((s) => s.selectedAdminUserEmail);
  const invitedAdminList = useAuthStore((s) => s.invitedAdminList);
  const fetchInvitedAdminList = useAuthStore((s) => s.fetchInvitedAdminList);
  const setSelectedAdmin = useAuthStore((s) => s.setSelectedAdmin);
  const signOut = useAuthStore((s) => s.signOut);
  const [showPlans, setShowPlans] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // "I have purchased" — wipes the ua_cache_* keys (incl. ua_cache_trial_start
  // which seeds trial-store synchronously on next boot, otherwise the user
  // would see the paywall again before the fresh Firestore read arrives) +
  // proxy cache, then hard-reloads. After reload, settings-store re-reads
  // the trial timestamp + license API returns fresh, so the paywall
  // dismisses if the purchase actually went through.
  const handleIHavePurchased = () => {
    if (refreshing) return;
    setRefreshing(true);
    try { clearAllCache(); } catch { /* */ }
    // Small delay so the spinner renders before the reload kicks in.
    setTimeout(() => { window.location.reload(); }, 200);
  };

  // CF-backed admin lookup with invitedAdminList → memory → IDB → CF chain.
  // Needed because cold reloads restore `selectedAdminUserId` from
  // localStorage / token claims via setSelectedAdminSilent(uid, null) — the
  // email isn't cached locally, so `selectedAdminUserEmail` is null until
  // settings-store finishes loading the personal-prefs Firestore doc.
  // Without this hook the paywall renders "your admin" instead of the email.
  const adminDetails = useAdminDetails();

  // Lazy-load invitedAdminList so the Switch organization block can render.
  // Cheap (one Firestore read, in-memory cached after first call) and only
  // fired when the paywall is actually visible.
  useEffect(() => {
    if (shouldShowPaywall && invitedAdminList === null) {
      fetchInvitedAdminList().catch(() => {});
    }
  }, [shouldShowPaywall, invitedAdminList, fetchInvitedAdminList]);

  // Filter out the currently-selected admin — shows other organizations the
  // user can switch into. Mirrors credits-blocker-dialog's behaviour: useful
  // both for shared users (try a different organization with an active sub)
  // AND own-account users whose own trial expired (jump into a shared org).
  const otherAdmins = useMemo(() => {
    if (!invitedAdminList) return [];
    return Object.values(invitedAdminList).filter(
      (a) => a.invitedAdminUserID !== selectedAdminUserId,
    );
  }, [invitedAdminList, selectedAdminUserId]);

  if (!shouldShowPaywall) return null;

  const daysAgo = Math.abs(trialDaysRemaining);

  return (
    <div
      data-testid="trial-paywall"
      data-paywall-kind={hasCancelledSubscription ? 'cancelled' : 'expired'}
      className="fixed inset-0 z-[10000000] overflow-y-auto bg-black/70 backdrop-blur-sm"
    >
      <div className="flex min-h-full items-center justify-center py-8 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="max-w-4xl w-full rounded-2xl bg-card p-8 shadow-2xl border border-border"
      >
        {/* Current user + Logout */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'User'}
                className="h-9 w-9 rounded-full object-cover border border-border shrink-0"
              />
            ) : (
              <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              {user?.displayName && (
                <p className="text-sm font-medium text-foreground truncate">
                  {user.displayName}
                </p>
              )}
              {user?.email && (
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => signOut()}
            className="text-muted-foreground hover:text-destructive shrink-0"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>

        {!showPlans ? (
          <>
            {/* Icon */}
            <div className="text-center">
              <motion.div
                initial={{ rotate: -8 }}
                animate={{ rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 10, delay: 0.15 }}
                className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10"
              >
                <Lock className="h-8 w-8 text-red-500" />
              </motion.div>

              {/* Title */}
              <h2 data-testid="paywall-title" className="font-display text-xl font-bold text-foreground mb-2">
                {hasCancelledSubscription
                  ? 'Your subscription was cancelled'
                  : 'Your free trial has expired'}
              </h2>

              {/* Description */}
              <p data-testid="paywall-description" className="text-sm text-muted-foreground mb-6">
                {isSharedAccess ? (
                  <>
                    {hasCancelledSubscription
                      ? null
                      : daysAgo === 0
                        ? 'Your trial ended today. '
                        : `Your trial ended ${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago. `}
                    Contact{' '}
                    <span className="font-medium text-foreground">
                      {adminDetails?.email || selectedAdminUserEmail || adminDetails?.name || 'your admin'}
                    </span>
                    {' '}to activate license for you
                  </>
                ) : (
                  <>
                    {hasCancelledSubscription
                      ? 'Please purchase a plan to resume using the app.'
                      : daysAgo === 0
                        ? 'Your trial ended today. Please purchase a plan to continue using the app.'
                        : `Your trial ended ${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago. Please purchase a plan to continue using the app.`}
                  </>
                )}
              </p>

              <div className="flex items-center justify-center gap-3 flex-wrap">
                {!isSharedAccess && (
                  <Button data-testid="paywall-purchase-cta" size="lg" onClick={() => setShowPlans(true)} className="px-8">
                    <ShoppingCart className="h-4 w-4" />
                    {hasCancelledSubscription ? 'Purchase Again' : 'Purchase Now'}
                  </Button>
                )}
                {/* "I have purchased" — clears ua_cache_* + proxy cache and
                    reloads so the trial / license state is re-fetched fresh.
                    Useful both for own-account users (after completing
                    Stripe checkout) and shared users (after their admin
                    activates the license for them on a separate device). */}
                <Button
                  data-testid="paywall-i-have-purchased"
                  size="lg"
                  variant="outline"
                  onClick={handleIHavePurchased}
                  disabled={refreshing}
                  className="px-6"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  I have purchased
                </Button>
              </div>
            </div>

            {/* Switch organization — shown whenever the user has at least one
                OTHER admin (organization) to fall back on. For shared users
                (Purchase button hidden) this is the primary action; for
                own-account users it's a useful fallback to jump into an org
                that already has an active sub. setSelectedAdmin reloads the
                page, after which the paywall re-evaluates and dismisses if
                the new org has trial / paid access. */}
            {otherAdmins.length > 0 && (
              <div data-testid="paywall-switch-org" className="mt-6 rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs font-medium text-foreground">
                    Switch organization
                  </p>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {otherAdmins.map((a) => {
                    const display = a.invitedAdminUserName || a.invitedAdminUserEmail || a.invitedAdminUserID;
                    const showEmailLine = a.invitedAdminUserEmail && a.invitedAdminUserEmail !== a.invitedAdminUserName;
                    return (
                      <button
                        key={a.invitedAdminUserID}
                        onClick={() => {
                          setSelectedAdmin(a.invitedAdminUserID, a.invitedAdminUserEmail || null).catch(() => {});
                        }}
                        className="w-full flex items-center gap-3 rounded-md border border-border bg-card hover:bg-muted px-3 py-2 transition-colors text-left"
                      >
                        {a.invitedAdminUserPic ? (
                          <img src={a.invitedAdminUserPic} alt="" className="h-8 w-8 rounded-full shrink-0" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-primary">
                              {(display || '?')[0]?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground truncate">{display}</p>
                          {showEmailLine && (
                            <p className="text-xs text-muted-foreground truncate">{a.invitedAdminUserEmail}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-left">
            <PlansBrowser />
          </div>
        )}

        {/* Sub-text */}
        <p className="mt-6 text-xs text-muted-foreground/70 text-center">
          Need help? Contact support@ulgebra.com
        </p>
      </motion.div>
      </div>
    </div>
  );
}
